/**
 * Sequence Enrollment Engine — the single owner of turning a cohort into a
 * governed, deduped, CRM-suppressed enrollment plan.
 *
 * Fixes the auditor's two hardest fails at once:
 *   • the MISSING SUPPRESSION MODULE — {@link evaluateSuppression} cross-
 *     checks replied/unsubscribed/bounced signals from activity, open deals,
 *     active tickets, churn-risk score, a recent-human-touch window over
 *     unified-timeline events, and existing active enrollments (dedupe),
 *     and attaches a provenance envelope explaining WHY;
 *   • the UNGOVERNED SEND PATH — no enrollment ever sends directly. A plan
 *     becomes {@link planToActionDiffs} (one rendered, approvable diff per
 *     first-step send) routed through {@link submitEnrollmentForApproval}
 *     into an approval queue as a single vetoed (pending) batch.
 *
 * Deliberately PURE: no store, no firebase, no clock reads unless the caller
 * omits `ctx.now`. Every CRM/db read is injected via {@link SuppressionContext}
 * as plain functions, so the engine is fully testable and the UI wires it to
 * real collections. Nothing here fabricates customer data — a missing signal
 * honestly means "no evidence to suppress on".
 *
 * Studied + reused (never re-implemented):
 *   • merge rendering:   src/lib/crm/mergeFields.ts (resolveMerge)
 *   • sequence model:    src/lib/crm/outreach.ts (Sequence/SequenceStep)
 *   • provenance seal:   src/lib/agent/provenance.ts (+ FNV digest)
 */

import {
  buildProvenance,
  deepLinkFor,
  provenanceDigest,
  type Confidence,
  type EvidenceRef,
} from '../agent/provenance';
import { fnvHash } from '../ledger/verifyChain';
import { resolveMerge, type MergeContext } from './mergeFields';
import type { Sequence, SequenceStep } from './outreach';

const DAY_MS = 86_400_000;

// ===========================================================================
// Schema
// ===========================================================================

/** Every reason a contact can be held out of an enrollment. */
export type SuppressionReason =
  | 'replied'
  | 'unsubscribed'
  | 'bounced'
  | 'open_deal'
  | 'active_ticket'
  | 'churn_risk'
  | 'recent_human_touch'
  | 'already_enrolled'
  | 'do_not_contact';

/** Canonical display/report order for stacked reasons. */
export const SUPPRESSION_REASON_ORDER: ReadonlyArray<SuppressionReason> = [
  'do_not_contact',
  'unsubscribed',
  'bounced',
  'replied',
  'open_deal',
  'active_ticket',
  'churn_risk',
  'recent_human_touch',
  'already_enrolled',
];

/** Human labels for the per-contact reason pills in the UI. */
export const SUPPRESSION_REASON_LABELS: Record<SuppressionReason, string> = {
  replied: 'Replied',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  open_deal: 'Open deal',
  active_ticket: 'Active ticket',
  churn_risk: 'Churn risk',
  recent_human_touch: 'Recent human touch',
  already_enrolled: 'Already enrolled',
  do_not_contact: 'Do not contact',
};

/**
 * The WHY behind a suppression decision — click-verifiable evidence plus a
 * confidence, sealed with the same FNV-1a digest the ledger chain uses, and
 * the human-readable reason lines the confirm card renders verbatim.
 */
export interface ProvenanceEnvelope {
  evidence: EvidenceRef[];
  confidence: Confidence;
  /** Plain-English "why" lines, one per matched (or cleared) check. */
  reasons: string[];
  /** Deterministic 'fnv1a:'-prefixed digest — ready to seal in the ledger. */
  digest: string;
}

/** The full, explained verdict for one contact. */
export interface SuppressionDecision {
  contactId: string;
  suppressed: boolean;
  reasons: SuppressionReason[];
  provenance: ProvenanceEnvelope;
}

/** One deduped, suppression-cleared, merge-resolved enrollee. */
export interface EnrollmentCandidate {
  contactId: string;
  email: string;
  accountId: string;
  sequenceId: string;
  /** MergeContext-shaped snapshot the send diffs render from. */
  mergeContext: Record<string, unknown>;
}

/** The governed output: who's in, who's held out and why, and the blast radius. */
export interface EnrollmentPlan {
  sequenceId: string;
  enrolled: EnrollmentCandidate[];
  suppressed: SuppressionDecision[];
  blastRadius: { contacts: number; accounts: number; domains: number };
}

// ===========================================================================
// Inputs — all CRM/db reads are injected as plain functions
// ===========================================================================

/** The minimal contact shape a cohort row must satisfy (Person-compatible). */
export interface EnrollmentContact {
  id: string;
  name?: string;
  email?: string;
  title?: string;
  /** Account the contact belongs to; falls back to `company`, then id. */
  accountId?: string;
  accountName?: string;
  company?: string;
  ownerName?: string;
  tags?: string[];
}

/** One normalized activity signal (derived by the caller from real data). */
export interface ActivitySignal {
  /** 'reply' | 'email' | 'unsubscribe' | 'bounce' | anything else. */
  kind: string;
  /** For email-kind signals: 'in' = inbound (a reply). */
  direction?: 'in' | 'out';
  at?: string | number;
  text?: string;
}

/** A timeline event — structurally satisfied by unifiedTimeline's TimelineEntry. */
export interface TouchEvent_ {
  /** Epoch millis. */
  at: number;
  actor?: { kind: 'human' | 'agent'; id: string };
}

/**
 * Everything suppression cross-checks, injected as plain functions of REAL
 * CRM data. Every function is optional: a missing lens honestly means "no
 * evidence for that check" — never a phantom suppression.
 */
export interface SuppressionContext {
  /** The sequence being enrolled into (dedupe target). */
  sequenceId?: string;
  /** Injected clock (epoch ms); wall clock when omitted. */
  now?: number;
  /** Churn score at/above which a contact is held out. Default 70. */
  churnRiskThreshold?: number;
  /** Days a human touch stays "recent". Default 7. */
  humanTouchWindowDays?: number;
  /** Activity signals (emails/replies/bounces/unsubscribes) for a contact. */
  activityFor?: (contactId: string) => ActivitySignal[];
  /** Does the contact (or their account) have an open deal in flight? */
  hasOpenDeal?: (contact: EnrollmentContact) => boolean;
  /** Does the contact's account have an active support ticket? */
  hasActiveTicket?: (contact: EnrollmentContact) => boolean;
  /** 0–100 churn risk for the contact's account; null = unknown. */
  churnRiskScore?: (contact: EnrollmentContact) => number | null;
  /** Unified-timeline events for the contact (human-touch window check). */
  timelineFor?: (contactId: string) => TouchEvent_[];
  /** Sequence ids the contact is already actively enrolled in. */
  activeEnrollments?: (contactId: string) => string[];
  /** Hard consent stops beyond the 'unsubscribed' tag. */
  isUnsubscribed?: (contact: EnrollmentContact) => boolean;
  isDoNotContact?: (contact: EnrollmentContact) => boolean;
}

// ===========================================================================
// evaluateSuppression
// ===========================================================================

const hasTag = (contact: EnrollmentContact, ...tags: string[]): boolean =>
  (contact.tags ?? []).some((t) => tags.includes(t.toLowerCase()));

const isReplySignal = (s: ActivitySignal): boolean =>
  s.kind === 'reply' || (s.kind === 'email' && s.direction === 'in');

/**
 * Should this contact be held out of the enrollment, and WHY?
 *
 * Pure and exhaustive: every check runs (reasons STACK — the operator sees
 * the full picture, not just the first blocker), and the decision carries a
 * provenance envelope with click-verifiable evidence, a confidence basis,
 * and a deterministic digest. A clean contact returns suppressed=false with
 * an empty reason list and provenance explaining what was checked.
 */
export function evaluateSuppression(
  contact: EnrollmentContact,
  ctx: SuppressionContext,
): SuppressionDecision {
  const now = ctx.now ?? Date.now();
  const reasons = new Set<SuppressionReason>();
  const lines: string[] = [];
  const evidence: EvidenceRef[] = [];

  const accountId = contact.accountId ?? contact.company ?? contact.id;
  evidence.push({
    kind: 'account',
    id: accountId,
    label: contact.accountName ?? contact.company ?? contact.name ?? contact.id,
    deepLink: deepLinkFor('account', accountId),
  });

  // --- Consent stops (hard) ------------------------------------------------
  if (ctx.isDoNotContact?.(contact) || hasTag(contact, 'do-not-contact', 'dnc')) {
    reasons.add('do_not_contact');
    lines.push('Contact is flagged do-not-contact.');
  }

  const activity = ctx.activityFor?.(contact.id) ?? [];
  if (
    ctx.isUnsubscribed?.(contact) ||
    hasTag(contact, 'unsubscribed') ||
    activity.some((s) => s.kind === 'unsubscribe')
  ) {
    reasons.add('unsubscribed');
    lines.push('Contact unsubscribed from outreach — hard legal stop.');
  }

  // --- Deliverability ------------------------------------------------------
  if (hasTag(contact, 'bounced') || activity.some((s) => s.kind === 'bounce')) {
    reasons.add('bounced');
    lines.push('Previous email to this address bounced.');
  }

  // --- Relationship stops ---------------------------------------------------
  const reply = activity.find(isReplySignal);
  if (reply) {
    reasons.add('replied');
    lines.push(
      `Contact already replied${reply.text ? ` ("${reply.text}")` : ''} — a human conversation is live.`,
    );
    if (typeof reply.at === 'string' || typeof reply.at === 'number') {
      evidence.push({
        kind: 'email',
        id: `${contact.id}:reply:${reply.at}`,
        label: 'Inbound reply',
        deepLink: deepLinkFor('email', `${contact.id}:reply:${reply.at}`),
      });
    }
  }

  if (ctx.hasOpenDeal?.(contact)) {
    reasons.add('open_deal');
    lines.push('An open deal is in flight — the owner runs this relationship.');
  }

  if (ctx.hasActiveTicket?.(contact)) {
    reasons.add('active_ticket');
    lines.push('The account has an active support ticket — cold outreach now would land badly.');
  }

  const churn = ctx.churnRiskScore?.(contact) ?? null;
  const threshold = ctx.churnRiskThreshold ?? 70;
  if (churn !== null && churn >= threshold) {
    reasons.add('churn_risk');
    lines.push(`Churn risk ${Math.round(churn)}/100 ≥ threshold ${threshold} — CS owns this account.`);
  }

  const windowDays = ctx.humanTouchWindowDays ?? 7;
  const touches = ctx.timelineFor?.(contact.id) ?? [];
  const lastHuman = touches
    .filter((t) => (t.actor?.kind ?? 'human') === 'human' && Number.isFinite(t.at))
    .reduce<number | null>((max, t) => (max === null || t.at > max ? t.at : max), null);
  if (lastHuman !== null && lastHuman <= now && now - lastHuman <= windowDays * DAY_MS) {
    const days = Math.floor((now - lastHuman) / DAY_MS);
    reasons.add('recent_human_touch');
    lines.push(
      `A human touched this contact ${days === 0 ? 'today' : `${days}d ago`} (within the ${windowDays}d window).`,
    );
  }

  // --- Dedupe ---------------------------------------------------------------
  const enrollments = ctx.activeEnrollments?.(contact.id) ?? [];
  const dupe = ctx.sequenceId
    ? enrollments.includes(ctx.sequenceId)
    : enrollments.length > 0;
  if (dupe) {
    reasons.add('already_enrolled');
    lines.push(
      ctx.sequenceId
        ? `Contact is already actively enrolled in sequence ${ctx.sequenceId}.`
        : 'Contact is already actively enrolled in another sequence.',
    );
  }

  const ordered = SUPPRESSION_REASON_ORDER.filter((r) => reasons.has(r));
  const suppressed = ordered.length > 0;
  if (!suppressed) {
    lines.push(
      'Cleared all CRM cross-checks: no reply, no unsubscribe, no bounce, no open deal, no active ticket, churn below threshold, no recent human touch, not already enrolled.',
    );
  }

  const normalized = buildProvenance({
    evidence,
    confidence: {
      // Deterministic rule evaluation over the supplied signals — the
      // uncertainty lives in signal coverage, not the rules themselves.
      score: suppressed ? 1 : 0.9,
      basis: suppressed
        ? `Suppressed by ${ordered.length} CRM check${ordered.length === 1 ? '' : 's'}: ${ordered.join(', ')}.`
        : 'No suppression signal matched across all injected CRM cross-checks.',
    },
  });

  return {
    contactId: contact.id,
    suppressed,
    reasons: ordered,
    provenance: {
      ...normalized,
      reasons: lines,
      digest: provenanceDigest(normalized),
    },
  };
}

// ===========================================================================
// buildEnrollmentPlan
// ===========================================================================

const emailDomain = (email: string): string | null => {
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return null;
  return email.slice(at + 1).toLowerCase();
};

/**
 * Map a savedView / filterEngine cohort into a governed plan: every contact
 * is evaluated against the injected suppression context (with the target
 * sequence pinned for dedupe), duplicates WITHIN the cohort are held out as
 * 'already_enrolled', merge context is resolved per candidate, and the blast
 * radius is computed as distinct contacts / accounts / email domains.
 * Pure — inputs are never mutated.
 */
export function buildEnrollmentPlan(
  cohort: EnrollmentContact[],
  sequence: Sequence,
  ctx: SuppressionContext = {},
): EnrollmentPlan {
  const effective: SuppressionContext = { ...ctx, sequenceId: ctx.sequenceId ?? sequence.id };

  const enrolled: EnrollmentCandidate[] = [];
  const suppressed: SuppressionDecision[] = [];
  const seenIds = new Set<string>();
  const seenEmails = new Set<string>();
  const accounts = new Set<string>();
  const domains = new Set<string>();

  for (const contact of cohort) {
    const email = (contact.email ?? '').trim().toLowerCase();

    // In-cohort dedupe: the same contact (by id or email) can only be
    // enrolled once per batch — the duplicate is an honest suppression.
    if (seenIds.has(contact.id) || (email !== '' && seenEmails.has(email))) {
      const base = evaluateSuppression(contact, {
        ...effective,
        activeEnrollments: () => [sequence.id],
      });
      suppressed.push(base);
      continue;
    }
    seenIds.add(contact.id);
    if (email !== '') seenEmails.add(email);

    const decision = evaluateSuppression(contact, effective);
    if (decision.suppressed) {
      suppressed.push(decision);
      continue;
    }

    const accountId = contact.accountId ?? contact.company ?? contact.id;
    const accountName = contact.accountName ?? contact.company;
    const mergeContext: MergeContext = {
      account: {
        ...(accountName !== undefined ? { name: accountName } : {}),
        ...(contact.ownerName !== undefined ? { ownerName: contact.ownerName } : {}),
      },
      contacts: [
        {
          ...(contact.name !== undefined ? { name: contact.name } : {}),
          ...(contact.email !== undefined ? { email: contact.email } : {}),
          ...(contact.title !== undefined ? { title: contact.title } : {}),
        },
      ],
    };

    enrolled.push({
      contactId: contact.id,
      email: contact.email ?? '',
      accountId,
      sequenceId: sequence.id,
      mergeContext: mergeContext as Record<string, unknown>,
    });
    accounts.add(accountId);
    const domain = email !== '' ? emailDomain(email) : null;
    if (domain) domains.add(domain);
  }

  return {
    sequenceId: sequence.id,
    enrolled,
    suppressed,
    blastRadius: {
      contacts: enrolled.length,
      accounts: accounts.size,
      domains: domains.size,
    },
  };
}

// ===========================================================================
// planToActionDiffs
// ===========================================================================

/**
 * One approvable, fully-rendered first-step send. This is the governance
 * trust mechanic: the operator sees the EXACT recipient, subject, body,
 * step and mailbox before anything is approved — never a blind blast.
 */
export interface ActionDiff {
  /** Deterministic id: send:<sequenceId>:<stepId>:<contactId>. */
  id: string;
  kind: 'sequence_send';
  /** All diffs from one plan share this key — they batch together. */
  batchKey: string;
  sequenceId: string;
  stepId: string;
  contactId: string;
  recipient: string;
  mailbox: string;
  /** Merge-rendered subject/body — never raw {{tokens}}, never "undefined". */
  subject: string;
  body: string;
  /** old → new summary line for the confirm card. */
  diff: string;
  /** Merge tokens that fell back — the approval surface warns on these. */
  missingMergeFields: string[];
}

/** The default mailbox label when the caller doesn't route one. */
export const DEFAULT_MAILBOX = 'primary-outbound';

/** The first step of a sequence: lowest dayOffset, declaration order ties. */
function firstStepOf(sequence: Sequence): SequenceStep | null {
  let first: SequenceStep | null = null;
  for (const step of sequence.steps) {
    if (first === null || step.dayOffset < first.dayOffset) first = step;
  }
  return first;
}

/**
 * Exactly one diff per ENROLLED candidate (suppressed contacts produce
 * nothing — they were held out with reasons, not silently dropped), with
 * subject/body rendered through the governed merge-field registry against
 * each candidate's resolved merge context. Pure; a sequence with no steps
 * honestly yields no sends.
 */
export function planToActionDiffs(
  plan: EnrollmentPlan,
  sequence: Sequence,
  opts: { mailbox?: string } = {},
): ActionDiff[] {
  const step = firstStepOf(sequence);
  if (!step) return [];
  const mailbox = opts.mailbox ?? DEFAULT_MAILBOX;
  const batchKey = `enroll:${plan.sequenceId}`;

  return plan.enrolled.map((candidate) => {
    const ctx = candidate.mergeContext as MergeContext;
    const subject = resolveMerge(step.subject ?? '', ctx);
    const body = resolveMerge(step.body ?? '', ctx);
    const missing = [...new Set([...subject.missing, ...body.missing])];
    return {
      id: `send:${plan.sequenceId}:${step.id}:${candidate.contactId}`,
      kind: 'sequence_send' as const,
      batchKey,
      sequenceId: plan.sequenceId,
      stepId: step.id,
      contactId: candidate.contactId,
      recipient: candidate.email,
      mailbox,
      subject: subject.text,
      body: body.text,
      diff: `outreach: none → step '${step.id}' of '${sequence.name}' to ${candidate.email || '—'} via ${mailbox}`,
      missingMergeFields: missing,
    };
  });
}

// ===========================================================================
// submitEnrollmentForApproval
// ===========================================================================

/** A single vetoed (pending-until-a-human-decides) batch of send diffs. */
export interface EnrollmentBatch {
  batchId: string;
  sequenceId: string;
  /** Vetoed by construction: nothing executes until a human approves. */
  status: 'pending';
  vetoable: true;
  items: ActionDiff[];
  suppressedCount: number;
  blastRadius: EnrollmentPlan['blastRadius'];
}

/** The only queue capability this engine needs — trivially fakeable. */
export interface EnrollmentApprovalQueue {
  push(batch: EnrollmentBatch): void | Promise<void>;
}

/** Deterministic batch id: same plan → same id (idempotent resubmits). */
export function enrollmentBatchId(plan: EnrollmentPlan): string {
  const contactIds = plan.enrolled.map((c) => c.contactId).sort();
  const seal = fnvHash('', JSON.stringify([plan.sequenceId, contactIds]));
  return `enroll:${plan.sequenceId}:${seal.replace(/^fnv1a:/, '')}`;
}

/**
 * Route a plan's send diffs into the approval queue as ONE vetoed batch —
 * the single governed entry point to the send path. When the sequence is
 * supplied the items are the fully-rendered {@link planToActionDiffs};
 * without it each candidate still becomes an (unrendered) diff so the
 * count and audit trail stay honest. Returns the deterministic batch id
 * plus the item count (= enrolled length by construction).
 */
export function submitEnrollmentForApproval(
  plan: EnrollmentPlan,
  queue: EnrollmentApprovalQueue,
  sequence?: Sequence,
): { batchId: string; itemCount: number } {
  const batchId = enrollmentBatchId(plan);
  const items: ActionDiff[] =
    sequence && sequence.steps.length > 0
      ? planToActionDiffs(plan, sequence)
      : plan.enrolled.map((candidate) => ({
          id: `send:${plan.sequenceId}:step-1:${candidate.contactId}`,
          kind: 'sequence_send' as const,
          batchKey: `enroll:${plan.sequenceId}`,
          sequenceId: plan.sequenceId,
          stepId: 'step-1',
          contactId: candidate.contactId,
          recipient: candidate.email,
          mailbox: DEFAULT_MAILBOX,
          subject: '',
          body: '',
          diff: `outreach: none → first step of ${plan.sequenceId} to ${candidate.email || '—'}`,
          missingMergeFields: [],
        }));

  queue.push({
    batchId,
    sequenceId: plan.sequenceId,
    status: 'pending',
    vetoable: true,
    items,
    suppressedCount: plan.suppressed.length,
    blastRadius: plan.blastRadius,
  });

  return { batchId, itemCount: items.length };
}
